import React, { useContext } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Grid, Typography, useTheme } from "@mui/material";
import { UsinasContext } from "../context/UsinasContextProvider";
import { PostUsina } from "../types";
import TextInput from "../../../main/components/TextInput";
import { supportDataToSupport } from "../supportMapper";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { emailRegex, phoneRegex } from "../../../main/utils/utils";
import PhoneNumberInput from "../../../main/components/PhoneNumberInput";
import CheckBoxTableRow from "../../users/components/CheckBoxTableRow";

const internetProviderSchema = yup.object().shape({
  internetProviderPhone: yup
    .string()
    .matches(phoneRegex, "Formato de Telefone inválido")
    .optional()
    .nullable(),
  internetProviderEmail: yup
    .string()
    .matches(emailRegex, "Forato de email inválido")
    .optional()
    .nullable(),
  internetProviderIP: yup.string().optional().nullable(),
});

const schema = yup.object().shape({
  internetProviders: yup
    .array()
    .of(internetProviderSchema)
    .optional()
    .nullable(),
  concessionairePhone: yup
    .string()
    .matches(phoneRegex, "Formato de Telefone inválido")
    .optional()
    .nullable(),
  concessionaireEmail: yup
    .string()
    .matches(emailRegex, "Forato de email inválido")
    .optional()
    .nullable(),
  localDataServerIP: yup.string().optional().nullable(),
  idLocalizacao: yup.string().optional().nullable(),
  dailyReportEnabled: yup.boolean().optional().nullable(),
  monthlyReportEnabled: yup.boolean().optional().nullable(),
  disponibilidadeOnOffEnabled: yup.boolean().optional().nullable(),
  partialReportEnabled: yup.boolean().optional().nullable(),
  phoneNumber: yup
    .string()
    .matches(phoneRegex, "Formato de Telefone inválido")
    .optional()
    .nullable(),
});

const sismetroIdsSchema = yup.object().shape({
  sismetroIdSolicitante: yup
    .string()
    .required("ID do solicitante é obrigatório"),
});

export type SupportData = yup.InferType<typeof schema>;
export type SismetroIdsData = yup.InferType<typeof sismetroIdsSchema>;
export type CombinedData = SupportData & SismetroIdsData;
export type InternetProviderData = yup.InferType<typeof internetProviderSchema>;

const combinedSchema = schema.concat(sismetroIdsSchema);

const SupportForm = React.forwardRef(
  (
    props: {
      selectedStep: number;
      setSelectedStep: React.Dispatch<React.SetStateAction<number>>;
    },
    ref
  ) => {
    const { newUsina, setNewUsina } = useContext(UsinasContext);
    const {
      handleSubmit,
      control,
      watch,
      setValue,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(combinedSchema),
      defaultValues: {
        internetProviders: [{}],
        dailyReportEnabled: true,
        monthlyReportEnabled: true,
        disponibilidadeOnOffEnabled: true
      },
    });
    const schemaData = watch();

    const {
      fields: internetProvidersFields,
      append: appendInternetProvider,
      remove: removeInternetProvider,
    } = useFieldArray({
      control,
      name: "internetProviders",
    });
    const onSubmit = async (data: CombinedData) => {
      const support = supportDataToSupport(data);
      const newUsinaData: PostUsina = {
        ...newUsina,
        support: support,
        reportOptions: {
          dailyReportEnabled: data.dailyReportEnabled ?? undefined,
          monthlyReportEnabled: data.monthlyReportEnabled ?? undefined,
          disponibilidadeOnOffEnabled: data.disponibilidadeOnOffEnabled ?? undefined,
          partialReportEnabled: data.partialReportEnabled ?? undefined
        },
        idSolicitante: data.sismetroIdSolicitante,
      };
      setNewUsina(newUsinaData);
      props.setSelectedStep(props.selectedStep + 1);
    };

    React.useImperativeHandle(ref, () => ({
      async submitForm() {
        await handleSubmit(onSubmit)();
      },
    }));
    const theme = useTheme();
    return (
      <Grid
        container
        component={"form"}
        width={"100%"}
        spacing={2}
        paddingX={"10px"}
        onSubmit={handleSubmit(onSubmit)}
      >
        <>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              Provedores de Internet
            </Typography>
          </Grid>

          <>
            {internetProvidersFields.map((field, index) => (
              <React.Fragment key={field.id}>
                <Grid item display={"flex"} xs={12}>
                  <Typography variant="subtitle1">
                    Provedor {index + 1}:
                  </Typography>
                </Grid>
                <Grid display={"flex"} item xs={12} md={3}>
                  <PhoneNumberInput
                    control={control}
                    fieldName={`internetProviders[${index}].internetProviderPhone`}
                    label="Telefone de Contato"
                    errors={errors}
                    sx={{ width: "100%", maxWidth: "280px" }}
                  />
                </Grid>
                <Grid display={"flex"} item xs={12} md={3}>
                  <TextInput
                    control={control}
                    fieldName={`internetProviders[${index}].internetProviderEmail`}
                    label="Email de Contato"
                    errors={errors}
                    sx={{ width: "100%", maxWidth: "280px" }}
                  />
                </Grid>
                <Grid display={"flex"} item xs={12} md={3}>
                  <TextInput
                    control={control}
                    fieldName={`internetProviders[${index}].internetProviderIP`}
                    label="IP"
                    errors={errors}
                    sx={{
                      width: "100%",
                      maxWidth: "280px",
                      marginBottom: "20px",
                    }}
                  />
                </Grid>
                {index > 0 && (
                  <Grid item display={"flex"} xs={12}>
                    <DangerBaseButton
                      onClick={() => {
                        removeInternetProvider(index);
                      }}
                      title="Remover "
                      // title="Remover Provedor"
                      startIcon={<DeleteIcon />}
                    />
                  </Grid>
                )}
              </React.Fragment>
            ))}
            <Grid
              item
              display={"flex"}
              xs={12}
              style={{
                justifyContent: "center",
              }}
            >
              <PrimaryBaseButton
                onClick={() => {
                  appendInternetProvider({});
                }}
                title="Adicionar Provedor"
                startIcon={<AddIcon />}
                sx={{
                  height: "38px",
                  [theme.breakpoints.up("md")]: { marginLeft: "auto" },
                }}
              // sx={{ height: "38px" }}
              />
            </Grid>
          </>
        </>

        <>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
                marginTop: "30px",
              }}
            >
              Concessionária de Energia
            </Typography>
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <PhoneNumberInput
              sx={{ width: "100%", maxWidth: "280px" }}
              control={control}
              fieldName="concessionairePhone"
              label="Telefone de Contato"
              errors={errors}
            // width={150}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px" }}
              control={control}
              fieldName="concessionaireEmail"
              label="Email de Contato"
              errors={errors}
            // width={150}
            />
          </Grid>
        </>

        <>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              IP do Servidor de Dados Local
            </Typography>
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px" }}
              control={control}
              fieldName="localDataServerIP"
              label="IP"
              errors={errors}
            // width={150}
            />
          </Grid>
        </>
        <>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              {" "}
              Sismetro
            </Typography>
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              required
              sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px" }}
              control={control}
              type="number"
              fieldName="sismetroIdSolicitante"
              label="ID Do Solicitante"
              errors={errors}
            />
          </Grid>
        </>
        <>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              ID da Localização
            </Typography>
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px" }}
              control={control}
              fieldName="idLocalizacao"
              label="ID"
              errors={errors}
            // width={150}
            />
          </Grid>
        </>
        <>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              Telefone da Usina
            </Typography>
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <PhoneNumberInput
              sx={{ width: "100%", maxWidth: "280px" }}
              control={control}
              fieldName="phoneNumber"
              label="Telefone"
              errors={errors}
            />
          </Grid>
        </>

        <>
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              Permissões de Relatórios
            </Typography>
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <CheckBoxTableRow flexDirection="row" active={schemaData.dailyReportEnabled ?? false} onChange={() => { setValue("dailyReportEnabled", !schemaData.dailyReportEnabled) }} name="Relatório Diário" />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <CheckBoxTableRow flexDirection="row" active={schemaData.monthlyReportEnabled ?? false} onChange={() => { setValue("monthlyReportEnabled", !schemaData.monthlyReportEnabled) }} name="Relatório Mensal" />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <CheckBoxTableRow flexDirection="row" active={schemaData.disponibilidadeOnOffEnabled ?? false} onChange={() => { setValue("disponibilidadeOnOffEnabled", !schemaData.disponibilidadeOnOffEnabled) }} name="Disponibilidade On-Off" />
          </Grid>
        </>
      </Grid>
    );
  }
);

export default SupportForm;
